import { NavLink, useLocation } from 'react-router-dom';
import { Fragment } from 'react';
import type { ComponentType, ReactNode } from 'react';

import { Logo } from '@/components/Logo';
import {
  HomeIcon,
  ClientesIcon,
  ComercialIcon,
  ComprasIcon,
  ManutencaoIcon,
  ProjetosIcon,
  FinanceiroIcon,
  HelpIcon,
  MegafoneIcon,
  RhIcon,
  ChevronRightIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
} from '@/components/icons';
import { useSidebar } from '@/contexts/SidebarContext';
import { cn } from '@/lib/cn';
import { useNavegacao } from '@/lib/navegacao/use-navegacao';
import type { IconeNav, ItemNav, SecaoNav } from '@/lib/navegacao/navegacao';

/**
 * Sidebar — navegação lateral fixa no desktop.
 *
 * Modos (regra "Sidebar colapsável" da memória feedback_layout_full_width.md):
 *   - Expandida (default): w-64 lg:w-72, layout completo com labels + sub-itens
 *   - Colapsada: w-16, só ícones centralizados, sub-items ocultos, tooltip nativo
 *
 * Toggle:
 *   - Botão no rodapé da Sidebar
 *   - Atalho global Ctrl+\ (Windows/Linux) / Cmd+\ (Mac) — implementado no SidebarProvider
 *     (B-03: era Ctrl+B, que colidia com "negrito" e recolhia o menu sem querer)
 *
 * Mobile (< md): sidebar continua hidden md:flex — MobileTabBar é a navegação.
 *
 * 🔴 MOBILE-COMPLETO (M1, 22/10/2026): O QUE aparece aqui não se decide mais
 * neste arquivo — vem de `useNavegacao` (`src/lib/navegacao/`), a MESMA fonte
 * da tela "Mais" do celular. Este arquivo só desenha. As regras de cada item
 * (Compras restrito ao setor, Monitoramento por flag + chave, Comercial por
 * chave, RH, Comunicados…) moram e estão comentadas em `navegacao.ts` e
 * `use-navegacao.ts`. ⚠️ O desenho é o mesmo de antes, classe por classe: a
 * comparação de capturas do computador (1440 e 1280) tem de dar zero.
 */

const ICONES: Record<IconeNav, ComponentType> = {
  home: HomeIcon,
  clientes: ClientesIcon,
  comercial: ComercialIcon,
  manutencao: ManutencaoIcon,
  projetos: ProjetosIcon,
  financeiro: FinanceiroIcon,
  compras: ComprasIcon,
  comunicados: MegafoneIcon,
  rh: RhIcon,
  ajuda: HelpIcon,
  configuracoes: AdminGearIcon,
};

export function Sidebar() {
  const { collapsed, toggle } = useSidebar();
  const location = useLocation();
  const secoes = useNavegacao();

  const renderItem = (item: ItemNav) => {
    const Icone = ICONES[item.icone];
    const principal = (
      <SidebarItem
        to={item.to}
        icon={<Icone />}
        label={item.label}
        end={item.end}
        partialMatch={item.partialMatch}
        collapsed={collapsed}
      />
    );
    if (item.forma === 'solto') return <Fragment key={item.chave}>{principal}</Fragment>;
    const aberto = !collapsed && item.abreEm.some((p) => location.pathname.startsWith(p));
    const subs = item.subItens.map((f) => (
      <SidebarSubItem key={f.to} to={f.to} label={f.label} testId={f.testId} />
    ));
    if (item.forma === 'dept') {
      return (
        <div key={item.to}>
          {principal}
          {aberto && item.subItens.length > 0 && (
            <div
              className="ml-3 mt-1 pl-4 border-l border-line-soft dark:border-line-soft-dark space-y-0.5"
              data-testid={`sidebar-sub-${item.slugDept}`}
            >
              {subs}
            </div>
          )}
        </div>
      );
    }
    return (
      <div key={`${item.to}-${item.testIdBloco}`} data-testid={item.testIdBloco}>
        {principal}
        {aberto && (
          <div className="ml-3 mt-1 pl-4 border-l border-line-soft dark:border-line-soft-dark space-y-0.5">
            {subs}
          </div>
        )}
      </div>
    );
  };

  const renderSecao = (s: SecaoNav) => (
    <div key={s.chave}>
      {s.titulo && !collapsed && <SectionHeader label={s.titulo} />}
      {s.titulo ? (
        <div className={cn('space-y-0.5', !collapsed && 'mt-2')}>{s.itens.map(renderItem)}</div>
      ) : (
        s.itens.map(renderItem)
      )}
    </div>
  );

  return (
    <aside
      data-testid="sidebar"
      data-collapsed={collapsed}
      className={cn(
        'hidden md:flex flex-col h-screen bg-paper-warm dark:bg-paper-dark-warm border-r border-line dark:border-line-dark fixed top-0 left-0 z-30 transition-all duration-200 ease-out',
        collapsed ? 'w-16' : 'w-64 lg:w-72'
      )}
    >
      {/* Logo / Marker */}
      <div
        className={cn(
          'flex items-center border-b border-line-soft dark:border-line-soft-dark',
          collapsed ? 'justify-center h-[60px]' : 'px-6 py-5'
        )}
      >
        {/* L4 (LAYOUT-CANONICO): recolhida, a marca era um "I" solto num
            círculo — lia como a logo cheia cortada (achado do smoke do
            Matheus). Agora é a marca reduzida de verdade. */}
        {collapsed ? <Logo variante="compacta" /> : <Logo size="md" />}
      </div>

      {/* Nav scrollável — Home · Operacional · Apoio · Sistema (só admin). */}
      <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-6">{secoes.map(renderSecao)}</nav>

      {/* Footer: toggle + versão */}
      <div className="border-t border-line-soft dark:border-line-soft-dark">
        <button
          type="button"
          onClick={toggle}
          data-testid="sidebar-toggle"
          aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
          title={`${collapsed ? "Expandir" : "Recolher"} menu (Ctrl+\\)`}
          className={cn(
            'flex items-center w-full px-3 py-3 text-ink-2 dark:text-ink-dark-2 hover:bg-paper dark:hover:bg-paper-dark-surface transition-colors',
            collapsed ? 'justify-center' : 'gap-3'
          )}
        >
          {collapsed ? <PanelLeftOpenIcon /> : <PanelLeftCloseIcon />}
          {!collapsed && <span className="text-xs">Recolher menu</span>}
        </button>
        {!collapsed && (
          <div className="px-4 py-2 text-xs text-ink-3 dark:text-ink-dark-3 border-t border-line-soft dark:border-line-soft-dark">
            {/* 🔴 24/09/2026: aqui dizia "IGM Security · v2". "v2" é a linguagem
                que o Matheus usa com o Cowork e comigo — o colaborador não sabe
                o que é. ⚠️ E estava no RODAPÉ DO MENU: em toda tela, para todo
                usuário, desde sempre. A varredura de vocabulário não pegou
                porque lia só as seis telas da Manutenção; quem pegou foi o
                print. */}
            IGM Security
          </div>
        )}
      </div>
    </aside>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    // FASE-B: 10px → 12px (piso tipográfico da maquete).
    <div className="px-3 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
      {label}
    </div>
  );
}

function SidebarItem({
  to,
  icon,
  label,
  chevron,
  end,
  partialMatch,
  collapsed,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  chevron?: boolean;
  end?: boolean;
  partialMatch?: string;
  collapsed: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      aria-label={label}
      title={collapsed ? label : undefined}
      className={({ isActive }) => {
        const matchesPartial = partialMatch
          ? window.location.pathname.startsWith(partialMatch)
          : false;
        const active = isActive || matchesPartial;

        if (collapsed) {
          return cn(
            'group flex items-center justify-center mx-auto rounded-lg transition-colors w-10 h-10',
            active
              ? 'bg-red text-white'
              : 'text-ink-1 dark:text-ink-dark-1 hover:bg-paper dark:hover:bg-paper-dark-surface'
          );
        }

        // FASE-B: ativo em semibold (padrão da maquete) — só peso, nada
        // estrutural muda no menu (condição 1 da aprovação).
        return cn(
          'group flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
          active
            ? 'bg-red-soft text-red-deep dark:bg-red-soft-dark dark:text-red-glow font-semibold'
            : 'text-ink-1 dark:text-ink-dark-1 font-medium hover:bg-paper dark:hover:bg-paper-dark-surface'
        );
      }}
    >
      <span className="flex-shrink-0 opacity-80 group-hover:opacity-100">{icon}</span>
      {!collapsed && <span className="flex-1">{label}</span>}
      {!collapsed && chevron && <ChevronRightIcon className="w-4 h-4 opacity-50" />}
    </NavLink>
  );
}

/**
 * Ícone "Configurações" (gear) pro item admin Sistema da sidebar.
 *
 * B-FINAL.6.3 (BUG-001) polish: SVG simplificado (era um gear complexo
 * que destoava do estilo dos outros ícones). Agora gear simples (circle
 * + 8 teeth) consistente com lucide-react `Settings` em estilo.
 */
function AdminGearIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M12 1v6m0 10v6m11-11h-6m-10 0H1m18.07-7.07-4.24 4.24m-9.66 9.66-4.24 4.24m18.14 0-4.24-4.24M5.93 5.93 1.69 1.69" />
    </svg>
  );
}

function SidebarSubItem({
  to,
  label,
  testId,
}: {
  to: string;
  label: string;
  testId?: string;
}) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        // FASE-B: 13px → 14px (escala única) + ativo red-deep semibold (AA).
        cn(
          'block px-3 py-1.5 rounded-md text-sm transition-colors',
          isActive
            ? 'text-red-deep dark:text-red-glow font-semibold'
            : 'text-ink-2 dark:text-ink-dark-2 hover:text-ink-1 dark:hover:text-ink-dark-1'
        )
      }
    >
      {label}
    </NavLink>
  );
}
